import { defineStore } from 'pinia';

interface IntentsListTableOptions {
  /** Current table page (1-based) */
  page: number;
  /** Rows per page */
  itemsPerPage: number;
  /** Column keys to sort by */
  sortBy: string[];
  /** Sort direction per sortBy entry (true = descending) */
  sortDesc: boolean[];
  /** Column keys to group by */
  groupBy: string[];
  /** Group direction per groupBy entry (true = descending) */
  groupDesc: boolean[];
  /** Whether the table must always be sorted */
  mustSort: boolean;
  /** Whether multi-column sorting is enabled */
  multiSort: boolean;
}

interface IntentsListState {
  /** Selected channel filter; 'all' or a channel id */
  selectedFilterChannel: string;
  /** Selected intent-type filter values */
  selectedFilterType: any[];
  /** Selected intent-status filter values */
  selectedFilterStatus: any[];
  /** Data-table options (Vuetify 2 data-table options shape) */
  tableOptions: IntentsListTableOptions;
  /** Current page of the intents list (1-based) */
  currentPage: number;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/intentsList.ts` (namespace
 * `intentsList`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `intentsList: state.intentsList` entry in `src/store/index.ts`.
 *
 * Collisions & renames:
 * - All five legacy getters (`selectedFilterChannel`, `selectedFilterType`,
 *   `selectedFilterStatus`, `tableOptions`, `currentPage`) were pure identity passthroughs of the
 *   state prop of the same name. Following the same precedent as `src/stores/app.ts`
 *   (`globalSnackbar`/`connectionLost`/`appVersion`), these are DROPPED entirely rather than
 *   renamed — Pinia exposes state props directly on the store instance
 *   (`useIntentsListStore().selectedFilterChannel`), which is exactly what the old getter
 *   returned. No `<name>Getter` renames needed since there is no conflicting action/mutation of
 *   the same name.
 * - The legacy `actions` registry was empty.
 * - The single legacy mutation (`setValue`) does not share a name with any getter/action, so it
 *   converts directly to a same-named action.
 */
export const useIntentsListStore = defineStore('intentsList', {
  state: (): IntentsListState => ({
    selectedFilterChannel: 'all',
    selectedFilterType: [],
    selectedFilterStatus: [],
    tableOptions: {
      page: 1,
      itemsPerPage: 20,
      sortBy: ['displayName'],
      sortDesc: [false],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
    currentPage: 1,
  }),
  actions: {
    /**
     * Converted from legacy mutation `setValue`. Generic key/value setter — key is dynamic, so
     * index untyped, exactly as in the legacy module.
     */
    setValue({ key, value }: { key: string; value: any }) {
      // generic key/value setter — key is dynamic, so index untyped
      (this as any)[key] = value;
    },
  },
  vuexPersist: {
    local: true,
  },
});
